import { useEffect, useRef, useState } from 'react';

/**
 * Captures Chrome/Edge/Brave's `beforeinstallprompt` event so we can show our own
 * "Install" button and trigger the native dialog on click. Only fires on Chromium
 * browsers when the PWA passes install criteria (manifest, HTTPS, SW, not installed).
 */

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

export function useAndroidInstall() {
  const [available, setAvailable] = useState(false);
  const [installing, setInstalling] = useState(false);
  const promptRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      promptRef.current = e as BeforeInstallPromptEvent;
      setAvailable(true);
    };
    const onInstalled = () => {
      promptRef.current = null;
      setAvailable(false);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    const evt = promptRef.current;
    if (!evt) return 'unavailable';
    setInstalling(true);
    try {
      await evt.prompt();
      const choice = await evt.userChoice;
      promptRef.current = null;
      setAvailable(false);
      return choice.outcome;
    } catch {
      return 'dismissed';
    } finally {
      setInstalling(false);
    }
  };

  return { available, installing, install };
}
